
import { defineComponent, computed } from "vue";
import { mapState } from "vuex";
import useFormatter from "@/use/formatter";
import Step from "@/components/shared/Step.vue";
import { $api } from "@/services/api";
import usePaymentOptions from "@/use/paymentOptions";
import NextButton from "@/components/shared/NextButton.vue";
import type { FormSchema } from "@/models/form";
import Modal from "@/components/shared/Modal.vue";
import { useStore } from "vuex";
import CircleLoader from "@/components/shared/CircleLoader.vue";
import useApplicationRatePlans from "@/composables/useApplicationRatePlans";
import type { RatePlanRepaymentVM, RatePlanVM } from "@/models/loanConfiguration";
import LoanOption from "@/components/loan-configuration/LoanOption.vue";

export default defineComponent({
  setup() {
    const store = useStore();

    const { formatNumber } = useFormatter();

    const {
      getRateOptionHeader,
      getRepaymentTermHeader,
      formatRatePercentages,
      formatStartOfQuarter,
    } = usePaymentOptions();

    const coApplicantTypeDisplayName = computed(() =>
      store.getters["coApplicantTypeDisplayName"].toLowerCase(),
    );

    const {
      error,
      loanConfigurationModule,
      previouslySelectedRatePlan,
      previouslySelectedRepaymentOption,
      selectedRepaymentOption,
      getRatePlansAsync,
      setSelectedRatePlan,
      setSelectedRepaymentOption,
    } = useApplicationRatePlans(store);

    return {
      formatNumber,
      getRateOptionHeader,
      getRepaymentTermHeader,
      formatRatePercentages,
      formatStartOfQuarter,
      coApplicantTypeDisplayName,
      loanConfigurationModule,
      error,
      previouslySelectedRatePlan,
      previouslySelectedRepaymentOption,
      selectedRepaymentOption,
      getRatePlansAsync,
      setSelectedRatePlan,
      setSelectedRepaymentOption,
      store,
    };
  },
  components: {
    Step,
    NextButton,
    Modal,
    CircleLoader,
    LoanOption,
  },
  props: {
    application: {
      type: Object,
      required: true,
    },
    applicant: {
      type: Object,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    isCurrent: {
      type: Boolean,
      required: false,
    },
    isCurrentSubStep: {
      type: Boolean,
      default: false,
    },
    initialDrawAmount: {
      type: Number,
      default: 0,
    },
  },
  data: () => ({
    currentSubStep: 3,
    fixedRepaymentTerm: 0,
    variableRepaymentTerm: 0,
    fixedRateInterestRate: 0,
    variableRateInterestRate: 0,
    repaymentTypes: [] as any,
    rateTypes: [] as any,
    interestRate: 0,
    loanTerm: 10,
    principal: 0,
    interestOnlyPayments: 0,
    originationFeeRate: 0,
    gettingRatePlans: true as boolean,
    selectedPaymentOption: undefined as undefined | RatePlanRepaymentVM,
    selectedRatePlan: undefined as undefined | RatePlanVM,
    selectedRepaymentTypeId: undefined as undefined | number,
    defaultRatePlans: [] as any[],
    appRatePlans: [] as RatePlanVM[],
    requestedAmount: undefined as undefined | number,
    schema: {
      paymentOption: {
        name: "paymentOption",
        type: "radio",
        required: true,
        hideLabel: true,
      },
    } as FormSchema,
  }),
  watch: {
    appRatePlans: {
      handler(newValue) {
        if (newValue && newValue.length > 0) {
          this.gettingRatePlans = false;
        }
      },
      immediate: true,
      deep: true,
    },
  },
  computed: {
    ...mapState(["appData", "lenderData"]),
    schoolSelected(): boolean | undefined {
      return this.appData.application?.schoolConfiguration?.schoolId !== null;
    },
    ratePlansExist(): boolean {
      return this.appRatePlans?.length > 0;
    },
  },
  methods: {
    goBack() {
      if (this.schoolSelected === true) {
        this.$emit("previous", true);
      } else {
        this.$emit("skipBorrowAmount", true);
      }
    },
    async getInitialDrawAmount() {
      // Check for initial draw info
      let initialDrawRequest = await $api.applications.getInitialDraw(this.appData.application.id);
      this.requestedAmount = initialDrawRequest?.amount || undefined;
    },
    updatePaymentOption(payment?: RatePlanRepaymentVM) {
      this.selectedPaymentOption = payment;

      this.setSelectedRepaymentOption(this.selectedPaymentOption);

      this.selectedRatePlan = this.appRatePlans?.find(
        (r: RatePlanVM) => r.id === payment?.ratePlanId,
      );

      this.setSelectedRatePlan(this.selectedRatePlan);
    },
    async selected() {
      // only updates if ratePlans (from DB) for the application exist
      await this.updateLoanConfiguration();

      if (this.schoolSelected) {
        this.$emit("complete", {
          schoolSelected: this.schoolSelected,
          selectedByApplicantId: this.applicant?.id,
          selectedPaymentOption: this.selectedPaymentOption,
          selectedRatePlanId: this.selectedRatePlan?.id,
        });
      } else {
        this.$emit("complete", {
          schoolSelected: this.schoolSelected,
          repaymentTypeId: this.selectedPaymentOption?.repaymentTypeId,
        });
      }
    },
    async goBackToPortal() {
      // If it is selected
      if (this.selectedPaymentOption) {
        this.updatePaymentOption(this.selectedPaymentOption);

        // Temporary until orchestration set up
        if ((this.appData.application?.ratePlans || []).length > 0) {
          // Get updated selected rate plan Id
          this.selectedRatePlan = this.appData?.application?.ratePlans.find(
            (a: any) => a.rateTypeId === this.selectedPaymentOption?.rateTypeId,
          );
          this.$emit("complete", {
            selectedPaymentOptiont: this.selectedPaymentOption,
            schoolSelected: this.schoolSelected,
            selectedByApplicantId: this.applicant?.id,
            selectedPaymentOption: this.selectedPaymentOption,
            selectedRatePlanId: this.selectedRatePlan?.id,
            interestRate: this.selectedRatePlan?.rate,
            rateTypeId: this.selectedPaymentOption?.rateTypeId,
          });
        }
      } else {
        this.$emit("complete", { error: true });
      }
    },
    async setRatePlans() {
      this.appRatePlans = this.loanConfigurationModule.appRatePlans;

      if (this.appRatePlans.length === 1) {
        this.appRatePlans[0].paymentOptionsActive = true;
      }
    },
    async updateLoanConfiguration() {
      // Get updated selected rate plan Id
      this.selectedRatePlan = this.appData?.application?.ratePlans.find(
        (a: any) => a.rateTypeId === this.selectedPaymentOption?.rateTypeId,
      );

      // Get updated selected rate plan I
      const loanConfiguration = {
        applicationId: this.appData.application.id,
        selectedRatePlanId: this.selectedRatePlan?.id,
        selectedByApplicantId: this.applicant?.id,
        repaymentTypeId: this.selectedPaymentOption?.repaymentTypeId,
      };

      let response = await $api.applications.addOrUpdateAppLoanConfig(loanConfiguration);

      if (response.error) {
        console.log("Error updating loan config");
      }
    },
    async setLoanConfiguration() {
      this.gettingRatePlans = true;

      this.updatePaymentOption(this.selectedRepaymentOption);

      this.selectedRatePlan = this.appRatePlans?.find(
        (r: RatePlanVM) =>
          r.id === this.loanConfigurationModule?.loanOptionsConfiguration?.selectedRatePlan?.id,
      );

      this.appRatePlans.forEach((r: RatePlanVM) => {
        if (r.id !== this.selectedRatePlan?.id) {
          r.paymentOptionsActive = false;
        } else {
          r.paymentOptionsActive = true;
        }
      });
    },
  },
  async mounted() {
    this.gettingRatePlans = true;

    await this.getRatePlansAsync();
    await this.setRatePlans();
    await this.getInitialDrawAmount();
    await this.setLoanConfiguration();
    this.gettingRatePlans = false;
  },
});
